'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUpRight, CircleCheckBig, ChevronLeft, ChevronRight } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } },
};

const fadeSlideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

function TabPill({ industry, isActive, onClick, pillRef }) {
    const Icon = ICONS[industry.icon];
    return (
        <motion.button
            ref={pillRef}
            variants={fadeSlideUp}
            onClick={onClick}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`
                relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
                border transition-all duration-300 cursor-pointer focus:outline-none
                shrink-0 snap-start whitespace-nowrap
                ${isActive
                    ? 'bg-primary border-primary text-white shadow-lg shadow-primary/30'
                    : 'bg-white/[0.04] border-white/10 text-slate-400 hover:text-white hover:bg-white/[0.08] hover:border-white/20'
                }
            `}
        >
            {Icon && <Icon size={14} />}
            {industry.title}
        </motion.button>
    );
}

function ServiceItem({ service, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06, duration: 0.35, ease: 'easeOut' }}
            className="group flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-primary/30 hover:bg-primary/[0.06] transition-all duration-200"
        >
            <CircleCheckBig size={14} className="text-primary shrink-0 group-hover:scale-120 transition-transform duration-200" />
            <span className="text-sm text-slate-300 group-hover:text-white transition-colors duration-200">
                {service}
            </span>
        </motion.div>
    );
}

function DetailPanel({ industry, isMobile, onNavigate }) {
    const Icon = ICONS[industry.icon];

    const handleDragEnd = (_, info) => {
        const SWIPE_THRESHOLD = 60;
        if (info.offset.x < -SWIPE_THRESHOLD) onNavigate?.(1);
        else if (info.offset.x > SWIPE_THRESHOLD) onNavigate?.(-1);
    };

    return (
        <motion.div
            key={industry.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            drag={isMobile ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={isMobile ? handleDragEnd : undefined}
            className="relative rounded-3xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm overflow-hidden p-6 lg:p-8 touch-pan-y"
        >
            <div className="absolute -top-20 -left-20 w-72 h-72 bg-primary/10 rounded-full blur-[90px] pointer-events-none" />
            <div className="absolute -bottom-20 -right-20 w-56 h-56 bg-primary/5 rounded-full blur-[70px] pointer-events-none" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                <div className="lg:col-span-2 space-y-5">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 shrink-0 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
                            {Icon && <Icon size={28} className="text-primary" />}
                        </div>
                        <div className="pt-1">
                            <h3 className="text-2xl font-bold text-white leading-tight">
                                {industry.title}
                            </h3>
                            {industry.subtitle && (
                                <p className="text-sm text-slate-400 mt-0.5">{industry.subtitle}</p>
                            )}
                        </div>
                    </div>

                    <p className="text-slate-300 text-sm md:text-md">
                        {industry.description}
                    </p>

                    {industry.link && industry.linkText && (
                        <Link
                            href={industry.link}
                            className="group/link inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-primary transition-colors duration-200"
                        >
                            {industry.linkText}
                            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                        </Link>
                    )}
                </div>

                <div className="lg:col-span-3">
                    <p className="text-sm text-slate-400 uppercase tracking-[0.1em] font-semibold mb-5">
                        Core Solutions
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {industry.services.map((service, i) => (
                            <ServiceItem key={service} service={service} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function IndustriesService({ data, design }) {
    const [activeId, setActiveId] = useState(data?.industries?.[0]?.id ?? '');
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    if (!data) return null;

    const activeIndustry = data.industries?.find((i) => i.id === activeId);
    const industries = data.industries ?? [];
    const activeIndex = Math.max(industries.findIndex((i) => i.id === activeId), 0);
    const ActiveIcon = activeIndustry ? ICONS[activeIndustry.icon] : null;

    const goToIndex = (nextIndex) => {
        const total = industries.length;
        if (!total) return;
        const industry = industries[(nextIndex + total) % total];
        if (industry) setActiveId(industry.id);
    };

    return (
        <section className="relative bg-[#05070f] py-12 md:py-20 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none select-none">
                <div
                    className="absolute inset-0 opacity-[0.035]"
                    style={{
                        backgroundImage:
                            'linear-gradient(rgba(255, 255, 255, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.45) 1px, transparent 1px)',
                        backgroundSize: '64px 64px',
                    }}
                />
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-primary/[0.08] rounded-full blur-[130px]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-primary/[0.05] rounded-full blur-[90px]" />
            </div>

            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    className="text-center mb-10"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.65, ease: 'easeOut' }}
                >
                    <h2 className="text-4xl md:text-4xl font-extrabold text-white">
                        {data.titleText}{' '}
                        <span className="text-primary">{data.coloredText}</span>
                    </h2>
                    <p className="mt-4 text-slate-400 max-w-3xl mx-auto text-sm md:text-base">
                        {data.description}
                    </p>
                </motion.div>

                {design == "content" && (
                    <div className='content-desgin-group'>
                        {/* Mobile: prev arrow — current tab — next arrow */}
                        <div className="md:hidden flex items-center justify-center gap-3 mb-6">
                            <button
                                type="button"
                                onClick={() => goToIndex(activeIndex - 1)}
                                aria-label="Previous industry"
                                className="w-9 h-9 rounded-full border border-white/15 bg-white/[0.04] flex items-center justify-center text-white shrink-0 cursor-pointer hover:bg-white/[0.08] transition-colors duration-200"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {activeIndustry && (
                                <span className="flex-1 min-w-0 max-w-[220px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-primary border border-primary text-white shadow-lg shadow-primary/30">
                                    {ActiveIcon && <ActiveIcon size={14} className="shrink-0" />}
                                    <span className="truncate">{activeIndustry.title}</span>
                                </span>
                            )}

                            <button
                                type="button"
                                onClick={() => goToIndex(activeIndex + 1)}
                                aria-label="Next industry"
                                className="w-9 h-9 rounded-full border border-white/15 bg-white/[0.04] flex items-center justify-center text-white shrink-0 cursor-pointer hover:bg-white/[0.08] transition-colors duration-200"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        {/* Desktop: full tab grid */}
                        <motion.div
                            className="hidden md:flex md:flex-wrap justify-center gap-2.5 mb-6"
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            {data.industries?.map((industry) => (
                                <TabPill
                                    key={industry.id}
                                    industry={industry}
                                    isActive={activeId === industry.id}
                                    onClick={() => setActiveId(industry.id)}
                                />
                            ))}
                        </motion.div>

                        <AnimatePresence mode="wait">
                            {activeIndustry && (
                                <DetailPanel
                                    key={activeId}
                                    industry={activeIndustry}
                                    isMobile={isMobile}
                                    onNavigate={(direction) => goToIndex(activeIndex + direction)}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {design == "icon" && (
                    <motion.div
                        className="grid grid-cols-4 sm:grid-cols-7 gap-2.5"
                        variants={stagger}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {data.industries?.map((industry) => {
                            const CardIcon = ICONS[industry.icon];
                            const isActive = activeId === industry.id;
                            return (
                                <motion.button
                                    key={industry.id}
                                    variants={fadeSlideUp}
                                    onClick={() => setActiveId(industry.id)}
                                    className={`
                                    group flex flex-col items-center gap-2 py-3 px-2 rounded-xl border
                                    transition-all duration-300 cursor-pointer focus:outline-none
                                    ${isActive
                                            ? 'border-primary/40 bg-primary/10'
                                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.06]'
                                        }
                                `}
                                >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${isActive ? 'bg-primary' : 'bg-white/10 group-hover:bg-white/30'}`}>
                                        {CardIcon && (
                                            <CardIcon
                                                size={14}
                                                className={`transition-colors duration-300 ${isActive ? 'text-white' : 'text-slate-300 group-hover:text-slate-200'}`}
                                            />
                                        )}
                                    </div>
                                    <span className={`text-sm font-medium text-center leading-tight transition-colors duration-300 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>
                                        {industry.title.split(' ')[0]}
                                    </span>
                                </motion.button>
                            );
                        })}
                    </motion.div>
                )}
            </div>
        </section>
    );
}
